import type { LoginResponse, MeResponse, Session, ShowcaseLoginOption } from "@academia-hub/shared-types";
import { apiFetch, apiGetList, apiGetResource, apiPost } from "../lib/api-client.js";

export interface LoginInput {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export function login(input: LoginInput): Promise<LoginResponse> {
  return apiPost<LoginResponse>("/auth/login", input);
}

/** The tenant's presentation logins, in the API's role order; empty when presentation logins are off. Public. */
export function fetchShowcaseLogins(): Promise<ShowcaseLoginOption[]> {
  return apiGetResource<{ logins: ShowcaseLoginOption[] }>("/auth/showcase-logins").then((data) => data.logins);
}

/** Signs in as a presentation login without a password — the same response (and cookies) as `login`. */
export function showcaseLogin(userId: string): Promise<LoginResponse> {
  return apiPost<LoginResponse>("/auth/showcase-login", { userId });
}

export function completeMfaChallenge(challengeToken: string, code: string): Promise<LoginResponse> {
  return apiPost<LoginResponse>("/auth/mfa/challenge", { challengeToken, code });
}

/** Bypasses `apiFetch`'s 401-retry-then-refresh logic — this IS the refresh call. */
export async function refresh(): Promise<{ accessToken: string; accessTokenExpiresInSeconds: number }> {
  const body = await apiFetch<{ data: { accessToken: string; accessTokenExpiresInSeconds: number } }>("/auth/refresh", { method: "POST" }, true);
  return body.data;
}

export function logout(): Promise<void> {
  return apiPost<{ loggedOut: true }>("/auth/logout").then(() => undefined);
}

export function logoutAll(): Promise<void> {
  return apiPost<{ loggedOut: true }>("/auth/logout-all").then(() => undefined);
}

export function forgotPassword(email: string): Promise<void> {
  return apiPost<{ requested: true }>("/auth/forgot-password", { email }).then(() => undefined);
}

export function resetPassword(token: string, newPassword: string): Promise<void> {
  return apiPost<{ reset: true }>("/auth/reset-password", { token, newPassword }).then(() => undefined);
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiPost<{ changed: true }>("/auth/change-password", { currentPassword, newPassword }).then(() => undefined);
}

export interface MfaSetup {
  secret: string;
  qrCodeDataUrl: string;
  recoveryCodes: string[];
}

export function beginMfaSetup(): Promise<MfaSetup> {
  return apiPost<MfaSetup>("/auth/mfa/setup");
}

export function verifyMfaSetup(code: string): Promise<void> {
  return apiPost<{ enabled: true }>("/auth/mfa/verify", { code }).then(() => undefined);
}

export function disableMfa(password: string): Promise<void> {
  return apiPost<{ disabled: true }>("/auth/mfa/disable", { password }).then(() => undefined);
}

export function fetchMe(): Promise<MeResponse> {
  return apiGetResource<MeResponse>("/auth/me");
}

export function fetchSessions(): Promise<Session[]> {
  return apiGetList<Session>("/auth/sessions").then((body) => body.data);
}

export function revokeSession(id: string): Promise<void> {
  return apiFetch<{ data: { revoked: boolean } }>(`/auth/sessions/${encodeURIComponent(id)}`, {
    method: "DELETE",
  }).then(() => undefined);
}
